import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { NgForOf, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';

import { WindowDetectService } from '@service/window-detect.service';
import { ImageLoadingComponent } from '@common/image-loading/image-loading.component';
import { HintsDisplay, NewLineToHtml, SafeHTML } from '@common/pipes';
import { Image } from '@proto/store/api/common/common';
import { Product } from '@proto/store/api/store/product';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';
import { PriceColorDisplayPipe, ProductInfoDisplayPipe } from './pipes';

export enum InfoType {
  PRODUCT_DETAIL,
  SHIPPING_INFO,
  DELIVERY_INFO,
  SIZE_TABLE,
  FITTING_TABLE,
}

interface IDialogData {
  type: InfoType;
  product: Product;
}

@Component({
  selector: 'app-product-info',
  templateUrl: './product-info.component.html',
  styleUrls: ['./product-info.component.scss'],
  imports: [
    ImageLoadingComponent,
    NewLineToHtml,
    SafeHTML,
    NgIf,
    NgForOf,
    NgSwitch,
    NgSwitchCase,
    HintsDisplay,
    NgForOf,
    FocusRemoverDirective,
    PriceColorDisplayPipe,
    ProductInfoDisplayPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SafeHTML],
})
export class ProductInfoComponent {
  readonly data: IDialogData = inject(MAT_BOTTOM_SHEET_DATA);
  readonly infoType = InfoType;
  readonly sizeImgWidth = computed(() => {
    if (this.windowDetectService.isTablet()) {
      return Math.min(this.windowDetectService.windowWidth(), 400) - 40;
    }
    return this.windowDetectService.windowWidth() - 40;
  });

  constructor(
    private windowDetectService: WindowDetectService,
    public bottomSheetRef: MatBottomSheetRef<ProductInfoComponent>
  ) {}

  static open(
    bottomSheet: MatBottomSheet,
    type: InfoType,
    product: Product,
    isTablet: boolean
  ): MatBottomSheetRef<ProductInfoComponent> {
    const data: IDialogData = { type, product: Product.fromPartial(product) };
    return bottomSheet.open(ProductInfoComponent, {
      panelClass: isTablet ? 'tablet-bottom-sheet' : 'bottom-sheet',
      data,
    });
  }

  trackByFn(_: number, image: Image | undefined): string {
    return image?.hash ?? '';
  }

  onClose(): void {
    this.bottomSheetRef.dismiss(undefined);
  }
}
